import {type MouseEvent, useCallback, useLayoutEffect, useRef} from "react";
import {FloatingFocusManager, FloatingOverlay, FloatingPortal, useDismiss, useFloating, useInteractions, useMergeRefs} from "@floating-ui/react";
import classNames from "classnames";

import {SearchComboboxArrowLeftIcon} from "../icons/SearchComboboxArrowLeftIcon";
import type {ISearchComboboxMobileLayerProps} from "../types/ISearchComboboxMobileLayerProps";

import * as styles from "./SearchComboboxMobileLayer.module.css";

const DEFAULT_LABEL = "Wyszukaj lokalizację";
const DEFAULT_BACK_LABEL = "Zamknij wyszukiwanie";

/**
 * Full-screen search layer: top bar with ← and the field slot, a scrollable results area and a footer pinned
 * above the safe area, both inside one listbox. Built on the Floating UI primitives used by `@pg-design/dialog-module`
 * (portal, overlay with scroll lock, modal focus trap). `@pg-design/modal-module` is not used: react-modal focuses its
 * content box after opening, which would take focus away from the input and close the iOS keyboard.
 */
export const SearchComboboxMobileLayer = (props: ISearchComboboxMobileLayerProps) => {
    const {
        isOpen,
        onClose,
        field,
        listboxProps,
        children,
        footer,
        initialFocusRef,
        returnFocusRef,
        layerRef,
        label = DEFAULT_LABEL,
        backLabel = DEFAULT_BACK_LABEL,
        className,
        dataTestId,
        backDataTestId
    } = props;

    const fieldSlotRef = useRef<HTMLDivElement>(null);
    const initialFocusTargetRef = useRef<HTMLElement | null>(null);
    const previouslyFocusedRef = useRef<HTMLElement | null>(null);

    // One close request per opening: the consumer may pair `onClose` with `history.back()`, so a double tap on ← or
    // Esc handled by several listeners must not close twice.
    const isCloseRequestedRef = useRef(false);

    useLayoutEffect(() => {
        if (isOpen) {
            isCloseRequestedRef.current = false;
        }
    }, [isOpen]);

    const requestClose = useCallback(() => {
        if (isCloseRequestedRef.current) {
            return;
        }

        isCloseRequestedRef.current = true;
        onClose();
    }, [onClose]);

    const {refs, context} = useFloating({
        open: isOpen,
        onOpenChange: (nextIsOpen) => {
            if (!nextIsOpen) {
                requestClose();
            }
        }
    });
    const dismiss = useDismiss(context, {outsidePress: false});
    const {getFloatingProps} = useInteractions([dismiss]);

    /*
     * Runs when the dialog element attaches, i.e. in the same commit that opens the layer. iOS raises the keyboard only
     * for a focus() made during the tap, and Floating UI defers its initial focus to an animation frame - so the input
     * is focused here, synchronously. Floating UI then sees focus inside and leaves it alone.
     */
    const focusOnOpen = useCallback(
        (element: HTMLDivElement | null) => {
            if (!element) {
                return;
            }

            const {activeElement, body} = element.ownerDocument;
            const isFocusInside = element.contains(activeElement);

            if (!isFocusInside && activeElement instanceof HTMLElement && activeElement !== body) {
                previouslyFocusedRef.current = activeElement;
            }

            const target = initialFocusRef?.current ?? fieldSlotRef.current?.querySelector<HTMLElement>("input, textarea") ?? null;
            initialFocusTargetRef.current = target;

            if (target && !isFocusInside) {
                target.focus({preventScroll: true});
            }
        },
        [initialFocusRef]
    );

    const dialogRef = useMergeRefs([refs.setFloating, layerRef, focusOnOpen]);

    // A press on the bar's padding would move focus to the dialog and close the on-screen keyboard.
    const onTopBarMouseDown = (event: MouseEvent<HTMLDivElement>) => {
        if (event.target === event.currentTarget) {
            event.preventDefault();
        }
    };

    if (!isOpen) {
        return null;
    }

    return (
        <FloatingPortal>
            <FloatingOverlay className={styles.overlay} lockScroll>
                <FloatingFocusManager context={context} initialFocus={initialFocusTargetRef} returnFocus={returnFocusRef ?? previouslyFocusedRef}>
                    <div
                        {...getFloatingProps()}
                        ref={dialogRef}
                        role="dialog"
                        aria-modal="true"
                        aria-label={label}
                        className={classNames(styles.layer, className)}
                        data-testid={dataTestId}
                    >
                        <div className={styles.topBar} onMouseDown={onTopBarMouseDown}>
                            <button type="button" className={styles.backButton} onClick={requestClose} aria-label={backLabel} data-testid={backDataTestId}>
                                <SearchComboboxArrowLeftIcon />
                            </button>
                            <div ref={fieldSlotRef} className={styles.fieldSlot}>
                                {field}
                            </div>
                        </div>

                        <div {...listboxProps} className={styles.listbox}>
                            <div className={styles.scrollArea}>{children}</div>
                            {footer ? <div className={styles.footer}>{footer}</div> : null}
                        </div>
                    </div>
                </FloatingFocusManager>
            </FloatingOverlay>
        </FloatingPortal>
    );
};
import {type MouseEvent, useCallback, useLayoutEffect, useRef} from "react";
import {FloatingFocusManager, FloatingOverlay, FloatingPortal, useDismiss, useFloating, useInteractions, useMergeRefs} from "@floating-ui/react";
import classNames from "classnames";

import {SearchComboboxArrowLeftIcon} from "../icons/SearchComboboxArrowLeftIcon";
import type {ISearchComboboxMobileLayerProps} from "../types/ISearchComboboxMobileLayerProps";

import * as styles from "./SearchComboboxMobileLayer.module.css";

const DEFAULT_LABEL = "Wyszukaj lokalizację";
const DEFAULT_BACK_LABEL = "Zamknij wyszukiwanie";

/**
 * Full-screen search layer: top bar with ← and the field slot, a scrollable results area and a footer pinned
 * above the safe area, both inside one listbox. Built on the Floating UI primitives used by `@pg-design/dialog-module`
 * (portal, overlay with scroll lock, modal focus trap). `@pg-design/modal-module` is not used: react-modal focuses its
 * content box after opening, which would take focus away from the input and close the iOS keyboard.
 */
export const SearchComboboxMobileLayer = (props: ISearchComboboxMobileLayerProps) => {
    const {
        isOpen,
        onClose,
        field,
        listboxProps,
        children,
        footer,
        initialFocusRef,
        returnFocusRef,
        layerRef,
        label = DEFAULT_LABEL,
        backLabel = DEFAULT_BACK_LABEL,
        className,
        dataTestId,
        backDataTestId
    } = props;

    const fieldSlotRef = useRef<HTMLDivElement>(null);
    const initialFocusTargetRef = useRef<HTMLElement | null>(null);
    const previouslyFocusedRef = useRef<HTMLElement | null>(null);

    // One close request per opening: the consumer may pair `onClose` with `history.back()`, so a double tap on ← or
    // Esc handled by several listeners must not close twice.
    const isCloseRequestedRef = useRef(false);

    useLayoutEffect(() => {
        if (isOpen) {
            isCloseRequestedRef.current = false;
        }
    }, [isOpen]);

    const requestClose = useCallback(() => {
        if (isCloseRequestedRef.current) {
            return;
        }

        isCloseRequestedRef.current = true;
        onClose();
    }, [onClose]);

    const {refs, context} = useFloating({
        open: isOpen,
        onOpenChange: (nextIsOpen) => {
            if (!nextIsOpen) {
                requestClose();
            }
        }
    });
    const dismiss = useDismiss(context, {outsidePress: false});
    const {getFloatingProps} = useInteractions([dismiss]);

    /*
     * Runs when the dialog element attaches, i.e. in the same commit that opens the layer. iOS raises the keyboard only
     * for a focus() made during the tap, and Floating UI defers its initial focus to an animation frame - so the input
     * is focused here, synchronously. Floating UI then sees focus inside and leaves it alone.
     */
    const focusOnOpen = useCallback(
        (element: HTMLDivElement | null) => {
            if (!element) {
                return;
            }

            const {activeElement, body} = element.ownerDocument;
            const isFocusInside = element.contains(activeElement);

            if (!isFocusInside && activeElement instanceof HTMLElement && activeElement !== body) {
                previouslyFocusedRef.current = activeElement;
            }

            const target = initialFocusRef?.current ?? fieldSlotRef.current?.querySelector<HTMLElement>("input, textarea") ?? null;
            initialFocusTargetRef.current = target;

            if (target && !isFocusInside) {
                target.focus({preventScroll: true});
            }
        },
        [initialFocusRef]
    );

    const dialogRef = useMergeRefs([refs.setFloating, layerRef, focusOnOpen]);

    // A press on the bar's padding would move focus to the dialog and close the on-screen keyboard.
    const onTopBarMouseDown = (event: MouseEvent<HTMLDivElement>) => {
        if (event.target === event.currentTarget) {
            event.preventDefault();
        }
    };

    if (!isOpen) {
        return null;
    }

    return (
        <FloatingPortal>
            <FloatingOverlay className={styles.overlay} lockScroll>
                <FloatingFocusManager context={context} initialFocus={initialFocusTargetRef} returnFocus={returnFocusRef ?? previouslyFocusedRef}>
                    <div
                        {...getFloatingProps()}
                        ref={dialogRef}
                        role="dialog"
                        aria-modal="true"
                        aria-label={label}
                        className={classNames(styles.layer, className)}
                        data-testid={dataTestId}
                    >
                        <div className={styles.topBar} onMouseDown={onTopBarMouseDown}>
                            <button type="button" className={styles.backButton} onClick={requestClose} aria-label={backLabel} data-testid={backDataTestId}>
                                <SearchComboboxArrowLeftIcon />
                            </button>
                            <div ref={fieldSlotRef} className={styles.fieldSlot}>
                                {field}
                            </div>
                        </div>

                        <div {...listboxProps} className={styles.listbox}>
                            <div className={styles.scrollArea}>{children}</div>
                            {footer ? <div className={styles.footer}>{footer}</div> : null}
                        </div>
                    </div>
                </FloatingFocusManager>
            </FloatingOverlay>
        </FloatingPortal>
    );
};
